import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Store, TrendingUp, Users, Shield, Wallet, Package, 
  Check, ArrowRight, Crown, Percent, Clock,
  Globe, Heart, Zap, Award, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import useAuthStore from '../store/authStore';

const BecomeVendor = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [openFaq, setOpenFaq] = useState(null);

  const isAlreadyVendor = user?.role === 'vendor';

  const benefits = [
    {
      icon: Wallet,
      title: 'Direkte Zahlungen',
      description: 'Kunden zahlen direkt an Sie. Keine Wartezeiten auf Auszahlungen.',
      highlight: true
    },
    {
      icon: Percent,
      title: 'Faire Provisionen',
      description: 'Nur 8% Provision (6% für Gold-Verkäufer). Keine versteckten Gebühren.',
      highlight: false
    },
    {
      icon: Store,
      title: 'Eigener Shop',
      description: 'Ihre individuelle Shop-Seite mit eigenem Design und Branding.',
      highlight: false
    },
    {
      icon: Users,
      title: 'Große Reichweite',
      description: 'Erreichen Sie tausende Kunden ohne eigene Marketing-Kosten.',
      highlight: false
    },
    {
      icon: Package,
      title: 'Einfache Verwaltung',
      description: 'Übersichtliches Dashboard für Produkte, Bestellungen und Statistiken.',
      highlight: false
    },
    {
      icon: Shield,
      title: 'Sicher & Vertrauenswürdig',
      description: 'Wir kümmern uns um Kundensupport und Vertrauensaufbau.',
      highlight: false
    }
  ];

  const goldFeatures = [
    { icon: Crown, text: 'Bis zu 200 Produkte (statt 50)' },
    { icon: Percent, text: 'Nur 6% Provision (statt 8%)' },
    { icon: Store, text: 'Individuelle Shop-Farben & Banner' },
    { icon: Zap, text: 'Drag & Drop Bilderverwaltung' },
    { icon: Award, text: 'Gold-Verkäufer Badge' },
    { icon: TrendingUp, text: 'Bevorzugte Platzierung in Suche' },
    { icon: Package, text: 'Produkt-Katalog Generator' },
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Kostenlos registrieren',
      description: 'Erstellen Sie Ihr Verkäuferkonto in wenigen Minuten. Keine Startgebühren.'
    },
    {
      step: 2,
      title: 'Shop einrichten',
      description: 'Laden Sie Ihre Produkte hoch und gestalten Sie Ihren Shop.'
    },
    {
      step: 3,
      title: 'Verkaufen & verdienen',
      description: 'Kunden bestellen, Sie versenden, das Geld landet direkt bei Ihnen.'
    }
  ];

  const faqs = [
    {
      question: 'Wie funktioniert die Bezahlung?',
      answer: 'Kunden zahlen direkt an Sie über Ihre hinterlegten Zahlungsmethoden (PayPal, Banküberweisung, etc.). Sie erhalten das Geld sofort, ohne Wartezeiten. Die Provision wird am Monatsende abgerechnet.'
    },
    {
      question: 'Welche Produkte kann ich verkaufen?',
      answer: 'Handgemachte Produkte, Kunst, Dekoration, Schmuck, Kerzen, Seifen und ähnliche Artikel. Keine Massenware oder verbotene Artikel.'
    },
    {
      question: 'Wie hoch sind die Kosten?',
      answer: 'Die Registrierung ist kostenlos. Wir berechnen 8% Provision auf verkaufte Artikel (6% für Gold-Verkäufer). Es gibt keine monatlichen Grundgebühren für das Basis-Paket.'
    },
    {
      question: 'Wie funktioniert der Versand?',
      answer: 'Sie sind selbst für den Versand verantwortlich. Sie legen Ihre Versandkosten pro Produkt fest. Bei Bestellungen erhalten Sie alle Lieferdetails.'
    },
    {
      question: 'Was ist das Gold-Paket?',
      answer: 'Für 4,99€/Monat erhalten Sie: bis zu 200 Produkte, nur 6% Provision, individuelle Shop-Gestaltung, Drag & Drop Bilderverwaltung, Gold-Badge und bevorzugte Platzierung.'
    },
    {
      question: 'Wie lange dauert die Freischaltung?',
      answer: 'Nach der Registrierung wird Ihr Konto innerhalb von 24-48 Stunden von unserem Team geprüft und freigeschaltet.'
    }
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      if (isAlreadyVendor) {
        navigate('/vendor-dashboard');
      } else {
        navigate('/vendor-register');
      }
    } else {
      navigate('/vendor-register');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#8B7355] via-[#A08060] to-[#8B7355] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Store className="h-5 w-5" />
              <span className="text-sm font-medium">Kostenlos starten</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Verkaufen Sie Ihre handgemachten Produkte
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Erreichen Sie tausende Kunden, erhalten Sie <strong>direkte Zahlungen</strong> und zahlen Sie nur <strong>8% Provision</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={handleGetStarted}
                className="bg-white text-[#8B7355] hover:bg-gray-100 text-lg px-8 py-6 h-auto"
              >
                {isAlreadyVendor ? 'Zum Dashboard' : 'Jetzt kostenlos starten'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
              >
                So funktioniert's
              </Button>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-300" />
                <span>Keine Startgebühren</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-300" />
                <span>Direkte Zahlungen</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-300" />
                <span>Nur 8% Provision</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Compact */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Warum auf Homeyoursoul verkaufen?
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg text-center ${benefit.highlight ? 'bg-[#8B7355]/10 border border-[#8B7355]/30' : 'bg-white border'}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                  benefit.highlight ? 'bg-[#8B7355] text-white' : 'bg-gray-100'
                }`}>
                  <benefit.icon className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold mb-1">{benefit.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works - Compact */}
      <section id="how-it-works" className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              In 3 Schritten zum Verkäufer
            </h2>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-left md:text-center md:flex-col">
                <div className="w-10 h-10 bg-[#8B7355] text-white rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div className="md:text-center">
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block text-gray-300 text-2xl">→</div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Button 
              size="lg"
              onClick={handleGetStarted}
              className="bg-[#8B7355] hover:bg-[#6D5A47] text-lg px-8"
            >
              {isAlreadyVendor ? 'Zum Dashboard' : 'Jetzt starten'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Gold Package - Compact */}
      <section className="py-10" style={{ background: 'linear-gradient(to bottom right, #FFFDF5, #FFF8E6)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Left: Title and Price */}
            <div className="text-center md:text-left md:w-1/3">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-2" style={{ backgroundColor: 'rgba(203, 170, 0, 0.15)', color: '#9A8200' }}>
                <Crown className="h-4 w-4" />
                <span className="text-sm font-medium">Premium</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                Gold-Verkäufer
              </h2>
              <div className="flex items-baseline justify-center md:justify-start gap-1">
                <span className="text-3xl font-bold" style={{ color: '#CBAA00' }}>4,99€</span>
                <span className="text-gray-500 text-sm">/ Monat</span>
              </div>
            </div>
            
            {/* Right: Features Grid */}
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {goldFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/60 rounded-lg p-2 border" style={{ borderColor: 'rgba(203, 170, 0, 0.3)' }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(203, 170, 0, 0.15)' }}>
                    <feature.icon className="h-3 w-3" style={{ color: '#CBAA00' }} />
                  </div>
                  <span className="text-xs text-gray-700 leading-tight">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Compact */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Häufig gestellte Fragen
            </h2>
          </div>
          
          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg border overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 text-sm">{faq.question}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-3 text-gray-600 text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#8B7355] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit, Ihre Produkte zu verkaufen?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Starten Sie noch heute kostenlos und erreichen Sie tausende neue Kunden.
          </p>
          <Button 
            size="lg"
            onClick={handleGetStarted}
            className="bg-white text-[#8B7355] hover:bg-gray-100 text-lg px-10 py-6 h-auto"
          >
            {isAlreadyVendor ? 'Zum Dashboard' : 'Jetzt kostenlos registrieren'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="mt-6 text-sm text-white/70">
            Keine Kreditkarte erforderlich • Keine Startgebühren • Jederzeit kündbar
          </p>
        </div>
      </section>
    </div>
  );
};

export default BecomeVendor;
